// src/api/axios.js
import axios from "axios"
import { toast } from "react-toastify"
import { getToken } from "utils/helpersUtils"

// Detect user's timezone dynamically
const getCurrentTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

const apiClient = axios.create({
  // baseURL: "http://localhost:8000/api", // Your Laravel API URL
  baseURL: process.env.REACT_APP_API, // Your Laravel API URL
  withCredentials: true, // Important for sessions/cookies if you use Sanctum
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
})

// Use a request interceptor to dynamically add the Authorization header
apiClient.interceptors.request.use(
  async config => {

    // Retrieve the token from local storage
    const token = getToken()

    // If a token exists, add it to the request headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // ✅ Add dynamic timezone header
    config.headers["X-Timezone"] = getCurrentTimezone()
    config.headers["X-Date"] = new Date().toISOString()

    return config // Important: return the config object
  },
  error => {
    // Handle request errors
    return Promise.reject(error)
  }
)

// ✅ Response interceptor for handling errors globally
apiClient.interceptors.response.use(
  response => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response
  },
  async error => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let errorMessage = "An unexpected error occurred."

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const status = error.response.status

      // Blob responses (e.g. PDF downloads) carry the JSON error body as a Blob.
      // Parse it so the real message surfaces instead of a generic fallback.
      let responseData = error.response.data
      if (
        responseData instanceof Blob &&
        responseData.type === "application/json"
      ) {
        try {
          responseData = JSON.parse(await responseData.text())
        } catch {
          responseData = undefined
        }
      }

      const dataMessage = responseData?.message
      const fieldErrors = responseData?.errors

      const flattenedFieldErrors =
        fieldErrors && typeof fieldErrors === "object"
          ? Object.values(fieldErrors)
              .flatMap(item =>
                Array.isArray(item) ? item : item ? [item] : []
              )
              .filter(Boolean)
          : []

      if (flattenedFieldErrors.length) {
        errorMessage = flattenedFieldErrors.join("\n")
      } else if (dataMessage) {
        errorMessage = dataMessage
      } else if (status === 401) {
        errorMessage = "Unauthorized. Please log in again."
      } else if (status === 403) {
        errorMessage = "Forbidden. You don't have permission."
      } else if (status === 404) {
        errorMessage = "Resource not found."
      } else if (status >= 500) {
        errorMessage = "A server error occurred. Please try again later."
      }
    } else if (error.request) {
      // The request was made but no response was received (e.g., network error)
      errorMessage = "Network error. Please check your connection."
    }

    // ✅ Only show toast if request doesn't explicitly disable it
    if (!error.config?.skipToast) {
      toast.error(errorMessage)
    }

    // It's important to reject the promise to allow individual .catch() blocks to run
    return Promise.reject(error)
  }
)

export default apiClient
